/**
 * 이메일 인증 관련 공통 함수들
 */

import type { RegistrationStatusResponse } from '@packages/types/auth/registration-status';
import { AUTH_ERRORS, parseApiError } from '@/utils/errors';
import { sendVerificationLink, getRegistrationStatus } from '../api/auth/useEmailVerification';

/**
 * 이메일 인증 링크 재발송 처리
 */
async function handleResendVerificationLink(
  email: string,
  setIsLinkSent: (value: boolean) => void,
  showSuccess: (title: string, message: string) => void,
  showError: (title: string, message: string) => void,
  t?: (key: string) => string,
  language?: string
): Promise<boolean> {
  try {
    const resendResponse = await sendVerificationLink({ email, language });

    if (resendResponse.success) {
      showSuccess(
        t
          ? t(AUTH_ERRORS.EMAIL_VERIFICATION_RESENT.title)
          : AUTH_ERRORS.EMAIL_VERIFICATION_RESENT.title,
        t
          ? t(AUTH_ERRORS.EMAIL_VERIFICATION_RESENT.message)
          : AUTH_ERRORS.EMAIL_VERIFICATION_RESENT.message
      );
      setIsLinkSent(true);
      return true;
    } else {
      const errorMessage = parseApiError({
        response: { data: { message: resendResponse.message } },
      });
      showError(errorMessage.title, errorMessage.message);
      return false;
    }
  } catch (error: unknown) {
    const errorMessage = parseApiError(error);
    showError(errorMessage.title, errorMessage.message);
    return false;
  }
}

/**
 * 이메일 인증 링크 발송 처리
 */
async function handleSendVerificationLink(
  email: string,
  setIsLinkSent: (value: boolean) => void,
  showSuccess: (title: string, message: string) => void,
  showError: (title: string, message: string) => void,
  t?: (key: string) => string,
  language?: string
): Promise<boolean> {
  try {
    const sendResponse = await sendVerificationLink({ email, language });

    // sendResponse가 없거나 유효하지 않은 경우
    if (!sendResponse || typeof sendResponse !== 'object') {
      showError(
        t ? t('auth.email_send_failed') : '이메일 발송 실패',
        t ? t('auth.email_send_failed') : '이메일 발송에 실패했습니다. 다시 시도해주세요.'
      );
      return false;
    }

    // success가 true인 경우 성공 처리
    if (sendResponse.success === true) {
      showSuccess(
        t
          ? t(AUTH_ERRORS.EMAIL_VERIFICATION_SENT.title)
          : AUTH_ERRORS.EMAIL_VERIFICATION_SENT.title,
        t
          ? t(AUTH_ERRORS.EMAIL_VERIFICATION_SENT.message)
          : AUTH_ERRORS.EMAIL_VERIFICATION_SENT.message
      );
      setIsLinkSent(true);
      return true;
    }

    // success가 false인 경우 에러 처리
    if (sendResponse.success === false) {
      const errorMessage = parseApiError({
        response: {
          data: { message: sendResponse.message || '이메일 발송에 실패했습니다.' },
        },
      });
      showError(errorMessage.title, errorMessage.message);
      return false;
    }

    // success 필드가 없는 경우 (예상치 못한 응답)
    console.warn('Unexpected response format:', sendResponse);
    showError(
      t ? t('auth.email_send_failed') : '이메일 발송 실패',
      t ? t('auth.email_send_failed') : '예상치 못한 응답 형식입니다. 다시 시도해주세요.'
    );
    return false;
  } catch (error: unknown) {
    // 네트워크 에러나 기타 예외 처리
    console.error('Error in handleSendVerificationLink:', error);

    // 이미 SendVerificationLinkResponse 형태의 에러 응답인 경우
    if (error && typeof error === 'object' && 'success' in error && error.success === false) {
      const errObj = error as { message?: string };
      const errorMessage = parseApiError({
        response: {
          data: { message: errObj.message || '이메일 발송에 실패했습니다.' },
        },
      });
      showError(errorMessage.title, errorMessage.message);
      return false;
    }

    // 일반적인 에러 처리
    const errorMessage = parseApiError(error);
    showError(errorMessage.title, errorMessage.message);
    return false;
  }
}

/**
 * 이메일 상태별 처리 로직
 */
async function handleEmailStatusCheck(
  email: string,
  registrationStatus: RegistrationStatusResponse,
  setIsEmailVerified: (verified: boolean) => void,
  setVerifiedEmail: (email: string) => void,
  setIsLinkSent: (sent: boolean) => void,
  showSuccess: (title: string, message: string) => void,
  showError: (title: string, message: string) => void,
  navigate: (path: string) => void,
  t?: (key: string) => string,
  language?: string,
  onAlreadySent?: () => void,
  onPendingApproval?: () => void
): Promise<boolean> {
  switch (registrationStatus.status) {
    case 'NOT_ALLOWED':
      showError(
        t ? t(AUTH_ERRORS.EMAIL_NOT_ALLOWED.title) : AUTH_ERRORS.EMAIL_NOT_ALLOWED.title,
        t
          ? t(AUTH_ERRORS.EMAIL_NOT_ALLOWED.message)
          : AUTH_ERRORS.EMAIL_NOT_ALLOWED.message
      );
      return false;

    case 'REJECTED':
      showError(
        t ? t(AUTH_ERRORS.EMAIL_REJECTED.title) : AUTH_ERRORS.EMAIL_REJECTED.title,
        t ? t(AUTH_ERRORS.EMAIL_REJECTED.message) : AUTH_ERRORS.EMAIL_REJECTED.message
      );
      return false;

    case 'PENDING_APPROVAL':
      if (onPendingApproval) {
        onPendingApproval();
      } else {
        showError(
          t ? t(AUTH_ERRORS.EMAIL_PENDING_APPROVAL.title) : AUTH_ERRORS.EMAIL_PENDING_APPROVAL.title,
          t
            ? t(AUTH_ERRORS.EMAIL_PENDING_APPROVAL.message)
            : AUTH_ERRORS.EMAIL_PENDING_APPROVAL.message
        );
      }
      return false;

    case 'REGISTERED':
      showError(
        t
          ? t(AUTH_ERRORS.EMAIL_ALREADY_REGISTERED.title)
          : AUTH_ERRORS.EMAIL_ALREADY_REGISTERED.title,
        t
          ? t(AUTH_ERRORS.EMAIL_ALREADY_REGISTERED.message)
          : AUTH_ERRORS.EMAIL_ALREADY_REGISTERED.message
      );
      navigate('/login');
      return false;

    case 'VERIFIED':
      showSuccess(
        t
          ? t(AUTH_ERRORS.EMAIL_VERIFICATION_COMPLETED.title)
          : AUTH_ERRORS.EMAIL_VERIFICATION_COMPLETED.title,
        t
          ? t(AUTH_ERRORS.EMAIL_VERIFICATION_COMPLETED.message)
          : AUTH_ERRORS.EMAIL_VERIFICATION_COMPLETED.message
      );
      setVerifiedEmail(email);
      setIsEmailVerified(true);
      setIsLinkSent(false);
      return true;

    case 'WAITING':
      if (onAlreadySent) {
        onAlreadySent();
      } else {
        showError(
          t
            ? t(AUTH_ERRORS.EMAIL_VERIFICATION_ALREADY_SENT.title)
            : AUTH_ERRORS.EMAIL_VERIFICATION_ALREADY_SENT.title,
          t
            ? t(AUTH_ERRORS.EMAIL_VERIFICATION_ALREADY_SENT.message)
            : AUTH_ERRORS.EMAIL_VERIFICATION_ALREADY_SENT.message
        );
      }
      return false;

    case 'WAITING_EXPIRED':
    case 'VERIFIED_EXPIRED':
      return await handleResendVerificationLink(email, setIsLinkSent, showSuccess, showError, t, language);

    case 'NOT_SENT':
      return await handleSendVerificationLink(email, setIsLinkSent, showSuccess, showError, t, language);

    default:
      showError(
        t ? t('auth.unknown_status') : '알 수 없는 상태',
        t
          ? t('auth.unknown_status_message')
          : '이메일 상태를 확인할 수 없습니다. 다시 시도해주세요.'
      );
      return false;
  }
}

/**
 * 이메일 인증 상태 확인 및 처리
 */
export async function handleEmailVerificationCheck(
  email: string,
  setIsEmailVerified: (verified: boolean) => void,
  setVerifiedEmail: (email: string) => void,
  setIsLinkSent: (sent: boolean) => void,
  showSuccess: (title: string, message: string) => void,
  showError: (title: string, message: string) => void,
  navigate: (path: string) => void,
  t?: (key: string) => string,
  language?: string,
  onAlreadySent?: () => void,
  onPendingApproval?: () => void
): Promise<boolean> {
  try {
    const registrationStatus = await getRegistrationStatus({ email });

    return await handleEmailStatusCheck(
      email,
      registrationStatus,
      setIsEmailVerified,
      setVerifiedEmail,
      setIsLinkSent,
      showSuccess,
      showError,
      navigate,
      t,
      language,
      onAlreadySent,
      onPendingApproval
    );
  } catch (error: unknown) {
    const errorMessage = parseApiError(error);
    showError(errorMessage.title, errorMessage.message);
    return false;
  }
}

/**
 * 회원가입 전 최종 이메일 인증 확인
 */
export async function validateEmailBeforeRegistration(
  email: string,
  verifiedEmail: string,
  setIsEmailVerified: (verified: boolean) => void,
  setIsLinkSent: (sent: boolean) => void,
  setVerifiedEmail: (email: string) => void,
  showError: (title: string, message: string) => void,
  t?: (key: string) => string
): Promise<{
  isValid: boolean;
  roleInfo?: {
    roleId?: number;
    companyName?: string;
    userName?: string;
  };
}> {
  try {
    const statusResponse = await getRegistrationStatus({ email });

    // 이메일 인증 상태 확인 (status가 VERIFIED인지 확인)
    if (statusResponse.status !== 'VERIFIED') {
      // 허용되지 않은 이메일인 경우 - 허가 요청 모달 표시
      if (statusResponse.status === 'NOT_ALLOWED') {
        showError(
          t ? t(AUTH_ERRORS.EMAIL_NOT_ALLOWED.title) : AUTH_ERRORS.EMAIL_NOT_ALLOWED.title,
          t
            ? t(AUTH_ERRORS.EMAIL_NOT_ALLOWED.message)
            : AUTH_ERRORS.EMAIL_NOT_ALLOWED.message
        );
        return { isValid: false };
      }

      // 거부된 이메일
      if (statusResponse.status === 'REJECTED') {
        showError(
          t ? t(AUTH_ERRORS.EMAIL_REJECTED.title) : AUTH_ERRORS.EMAIL_REJECTED.title,
          t ? t(AUTH_ERRORS.EMAIL_REJECTED.message) : AUTH_ERRORS.EMAIL_REJECTED.message
        );
        return { isValid: false };
      }

      // 승인 대기 중
      if (statusResponse.status === 'PENDING_APPROVAL') {
        showError(
          t
            ? t(AUTH_ERRORS.EMAIL_PENDING_APPROVAL.title)
            : AUTH_ERRORS.EMAIL_PENDING_APPROVAL.title,
          t
            ? t(AUTH_ERRORS.EMAIL_PENDING_APPROVAL.message)
            : AUTH_ERRORS.EMAIL_PENDING_APPROVAL.message
        );
        return { isValid: false };
      }

      // 인증 메일 미발송인 경우 - 인증 메일 발송 안내
      if (statusResponse.status === 'NOT_SENT') {
        showError(
          t ? t('auth.verification_required') : '이메일 인증 필요',
          t
            ? t('auth.verification_required_message')
            : '이메일 인증이 필요합니다. 이메일 옆의 "Send" 버튼을 클릭하여 인증 메일을 발송해주세요.'
        );
        return { isValid: false };
      }

      // 인증 대기 중
      if (statusResponse.status === 'WAITING') {
        showError(
          t
            ? t(AUTH_ERRORS.EMAIL_VERIFICATION_ALREADY_SENT.title)
            : AUTH_ERRORS.EMAIL_VERIFICATION_ALREADY_SENT.title,
          t
            ? t(AUTH_ERRORS.EMAIL_VERIFICATION_ALREADY_SENT.message)
            : AUTH_ERRORS.EMAIL_VERIFICATION_ALREADY_SENT.message
        );
        return { isValid: false };
      }

      // 인증 만료된 경우 특별 처리
      if (
        statusResponse.status === 'VERIFIED_EXPIRED' ||
        statusResponse.status === 'WAITING_EXPIRED'
      ) {
        showError(
          '인증 만료',
          '이메일 인증이 만료되었습니다. "Send" 버튼을 클릭하여 새로운 인증 메일을 발송해주세요.'
        );
        // 인증 상태 초기화
        setIsEmailVerified(false);
        setIsLinkSent(false);
        setVerifiedEmail('');
        return { isValid: false };
      }

      // 그 외의 경우
      showError(
        t
          ? t(AUTH_ERRORS.EMAIL_VERIFICATION_REQUIRED.title)
          : AUTH_ERRORS.EMAIL_VERIFICATION_REQUIRED.title,
        t
          ? t(AUTH_ERRORS.EMAIL_VERIFICATION_REQUIRED.message)
          : AUTH_ERRORS.EMAIL_VERIFICATION_REQUIRED.message
      );
      return { isValid: false };
    }

    // 인증된 이메일과 현재 입력 이메일 일치 확인
    // verifiedEmail 로컬 상태 대신 실제 쿼리한 email과 비교 (새로고침 등으로 상태가 초기화되어도 정상 동작)
    if (statusResponse.data?.email && statusResponse.data.email !== email) {
      showError(
        t ? t('auth.email_mismatch') : '이메일 불일치',
        t
          ? t('auth.email_mismatch_message')
          : '서버에 등록된 인증된 이메일 정보와 현재 인증된 이메일이 일치하지 않습니다. 다시 인증을 진행해주세요.'
      );
      setIsEmailVerified(false);
      setIsLinkSent(false);
      setVerifiedEmail('');
      return { isValid: false };
    }

    // 서버에서 VERIFIED 확인됐으므로 로컬 상태 동기화 (새로고침 후 상태가 초기화된 경우 복원)
    if (!verifiedEmail) {
      setVerifiedEmail(email);
      setIsEmailVerified(true);
    }

    return {
      isValid: true,
      roleInfo: {
        roleId: statusResponse.data?.allowedEmail?.role_id || undefined,
        companyName: statusResponse.data?.allowedEmail?.company_name || undefined,
        userName: statusResponse.data?.allowedEmail?.user_name || undefined,
      },
    };
  } catch (error: unknown) {
    const errorMessage = parseApiError(error);
    showError(errorMessage.title, errorMessage.message);
    return { isValid: false };
  }
}
